import React, { useEffect, useState } from "react";
import {
  Paper, Grid, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  makeStyles
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), margin: theme.spacing(2) },
  card: { height: "100%" },
  metric: { fontSize: 26, fontWeight: 600 },
  positive: { color: "#28a745" },
  negative: { color: "#dc3545" },
  series: { marginTop: theme.spacing(3) },
  bar: { height: 18, borderRadius: 3 }
}));

const fmt = (v, c = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: c });

const FinanceDashboard = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
    Promise.all([
      api.get("/finance/dashboard", { params: { from: first, to: last } }),
      api.get("/finance/cashflow-series", { params: { months: 6 } })
    ])
      .then(([d, s]) => { setData(d.data); setSeries(s.data || []); })
      .catch(() => toast.error("Erro ao carregar dashboard"));
  }, []);

  if (!data) return <Paper className={classes.root}><h2>Dashboard Financeiro</h2><p>Carregando…</p></Paper>;

  const maxSeries = Math.max(1, ...series.flatMap((s) => [s.income, s.expense]));

  return (
    <Paper className={classes.root}>
      <h2>Dashboard Financeiro</h2>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography color="textSecondary">Saldo total</Typography>
              <div className={classes.metric}>{fmt(data.totalBalance)}</div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography color="textSecondary">Entradas (mês)</Typography>
              <div className={`${classes.metric} ${classes.positive}`}>{fmt(data.cashFlow.income)}</div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography color="textSecondary">Saídas (mês)</Typography>
              <div className={`${classes.metric} ${classes.negative}`}>{fmt(data.cashFlow.expense)}</div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card}>
            <CardContent>
              <Typography color="textSecondary">Vencidos</Typography>
              <div className={classes.metric}>
                <span className={classes.negative}>{data.overduePayables}</span>
                {" / "}
                <span className={classes.negative}>{data.overdueReceivables}</span>
              </div>
              <Typography variant="caption">a pagar / a receber</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 16 }}>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6">Saldo por conta</Typography>
              <Table size="small">
                <TableBody>
                  {data.accounts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell><small>{a.type}</small></TableCell>
                      <TableCell align="right">{fmt(a.currentBalance, a.currency)}</TableCell>
                    </TableRow>
                  ))}
                  {data.accounts.length === 0 && (
                    <TableRow><TableCell colSpan={3}>Nenhuma conta.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6">Próximos a receber (30 dias)</Typography>
              <Table size="small">
                <TableBody>
                  {data.upcomingReceivables.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.dueDate}</TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell align="right">{fmt(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {data.upcomingReceivables.length === 0 && (
                    <TableRow><TableCell colSpan={3}>Sem vencimentos próximos.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="h6" style={{ marginTop: 12 }}>Próximos a pagar (30 dias)</Typography>
              <Table size="small">
                <TableBody>
                  {data.upcomingPayables.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.dueDate}</TableCell>
                      <TableCell>{p.description}</TableCell>
                      <TableCell align="right">{fmt(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {data.upcomingPayables.length === 0 && (
                    <TableRow><TableCell colSpan={3}>Sem vencimentos próximos.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card className={classes.series}>
        <CardContent>
          <Typography variant="h6">Fluxo de caixa — últimos meses</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mês</TableCell>
                <TableCell>Entradas</TableCell>
                <TableCell>Saídas</TableCell>
                <TableCell>Resultado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {series.map((s) => (
                <TableRow key={s.ym}>
                  <TableCell>{s.ym}</TableCell>
                  <TableCell>
                    <div className={classes.bar} style={{ width: `${(s.income / maxSeries) * 100}%`, background: "#28a745" }} />
                    {fmt(s.income)}
                  </TableCell>
                  <TableCell>
                    <div className={classes.bar} style={{ width: `${(s.expense / maxSeries) * 100}%`, background: "#dc3545" }} />
                    {fmt(s.expense)}
                  </TableCell>
                  <TableCell><strong>{fmt(s.income - s.expense)}</strong></TableCell>
                </TableRow>
              ))}
              {series.length === 0 && (
                <TableRow><TableCell colSpan={4}>Sem dados.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Paper>
  );
};

export default FinanceDashboard;
